import { Icon } from '../../../layout/Icon.jsx';
import { formatIQD } from '../../../shared/format.js';

const METHOD_LABEL = {
  cash: 'نقدي',
  card: 'بطاقة',
  card_balance: 'رصيد الكارت',
  credit: 'آجل',
  transfer: 'تحويل',
  external: 'خارجي'
};

export function SettleConfirm({ open, onClose, onConfirm, totals, payment, override, loading }) {
  if (!open) return null;

  const due = Number(totals?.due || 0);
  const isCredit = payment?.method === 'credit';
  // «آجل» لا يُحصّل شيئاً الآن مهما كُتب في خانة المبلغ — نفس ما يفعله
  // buildSettleBody قبل الإرسال، حتى لا تَعِد النافذة بمبلغ لا يُدفع.
  const paidNow = isCredit ? 0 : Number(payment?.paidAmount || 0);
  const change = Math.max(0, paidNow - due);
  const remaining = Math.max(0, due - paidNow);

  // مصير الفرق كما اختاره الكاشير فعلاً، وهو ما سيُرسل للخادم:
  //   waive → خصم إداري على رسوم اللعب، لا دين على العميل
  //   debt  → الفواتير تبقى مستحقة = دين حقيقي
  // كانت النافذة تقول «سيُسجَّل آجلاً» في الحالتين، فتناقض ما ينفّذه الخادم.
  const shortfall = !isCredit && remaining > 0 ? (payment?.shortfall || null) : null;
  const isWaive = shortfall === 'waive';
  const isDebt = shortfall === 'debt';
  const waiveReason = (override?.reason || '').trim();

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4"
      style={{ background: 'rgba(15,16,18,0.5)' }}
    >
      <div
        className="bg-white rounded-[14px] shadow-card max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-h">
          <h3 className="text-[15px] font-bold flex-1">تأكيد التسوية</h3>
          <button type="button" className="text-ink-3 hover:text-ink" onClick={onClose}>
            <Icon name="x" size={16} />
          </button>
        </div>
        <div className="p-4 space-y-2 text-[13px]">
          {Number(totals?.membersCount || 0) > 1 ? (
            <Row label="عدد الأطفال" value={`${totals.membersCount} أطفال`} bold />
          ) : null}
          <Row label="الإجمالي" value={formatIQD(totals?.grand || 0)} />
          {Number(totals?.discount || 0) > 0 ? (
            <Row label="خصم (رسوم اللعب)" value={`- ${formatIQD(totals.discount)}`} color="#15803D" />
          ) : null}
          {Number(totals?.rounding || 0) > 0 ? (
            <Row label="تقريب" value={`+ ${formatIQD(totals.rounding)}`} />
          ) : null}
          <Row label="مدفوع سابقاً" value={formatIQD(totals?.paid || 0)} />
          <Row label="المتبقّي" value={formatIQD(due)} bold />
          <div className="border-t border-line my-1" />
          <Row label="طريقة الدفع" value={METHOD_LABEL[payment?.method] || payment?.method || '—'} />
          <Row label="المبلغ المدفوع الآن" value={formatIQD(paidNow)} bold />
          {payment?.method === 'cash' && change > 0 ? (
            <Row label="الباقي للزبون" value={formatIQD(change)} color="#15803D" />
          ) : null}

          {/* «سيُسجَّل آجلاً» للآجل الحقيقي وحده: المبلغ يبقى ديناً على العميل. */}
          {isCredit && due > 0 ? (
            <Row label="سيُسجَّل آجلاً (دين على العميل)" value={formatIQD(due)} color="#B91C1C" bold />
          ) : null}
          {isDebt ? (
            <Row label="ناقص — سيُسجَّل آجلاً (دين على العميل)" value={formatIQD(remaining)} color="#B91C1C" bold />
          ) : null}
          {isWaive ? (
            <Row label="سيتم إسقاط الفرق" value={formatIQD(remaining)} color="#15803D" bold />
          ) : null}
          {!isCredit && remaining > 0 && !shortfall ? (
            <Row label="ناقص (بلا قرار)" value={formatIQD(remaining)} color="#B91C1C" />
          ) : null}

          {isWaive ? (
            <div className="mt-2 p-2 rounded-[10px] text-[12px]" style={{ background: '#F0FDF4' }}>
              يُسقَط الفرق <b>{formatIQD(remaining)}</b> خصماً على رسوم اللعب باسمك وتاريخه —
              <b> لا يُسجَّل ديناً على العميل ولا يظهر في ديون العملاء</b>.
              {waiveReason ? <div className="text-ink-3 mt-0.5">سبب الإسقاط: {waiveReason}</div> : null}
            </div>
          ) : null}
          {isDebt ? (
            <div className="mt-2 p-2 rounded-[10px] text-[12px]" style={{ background: '#FFF7ED' }}>
              يبقى <b>{formatIQD(remaining)}</b> مستحقاً على ولي الأمر ويظهر في شاشة ديون العملاء.
            </div>
          ) : null}

          {override?.active && Number(override.amount) > 0 ? (
            <div className="mt-2 p-2 rounded-[10px] bg-orange-soft text-[12px]">
              خصم: <b>{formatIQD(override.amount)}</b>
              {override.reason ? <div className="text-ink-3 mt-0.5">السبب: {override.reason}</div> : null}
            </div>
          ) : null}
        </div>
        <div className="card-h border-b-0 border-t flex gap-2">
          <button type="button" className="btn flex-1 justify-center" onClick={onClose} disabled={loading}>
            إلغاء
          </button>
          <button
            type="button"
            className="btn btn-success flex-1 justify-center"
            onClick={onConfirm}
            disabled={loading}
          >
            <Icon name="check" size={14} />
            {loading ? 'جاري التسوية...' : 'تأكيد التسوية'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, color, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-ink-3">{label}</span>
      <span className={bold ? 'font-extrabold' : 'font-semibold'} style={{ color }}>
        {value}
      </span>
    </div>
  );
}
